import moment from 'moment-timezone'

import type { Request } from '../../../../index'
import type {
  ComplexChangeAction,
  ComplexHistory,
  ComplexHistoryItem,
  ComplexHistoryRequestParams,
} from 'app/common/types/food/schools/complexes/history'


type ParamsApi = {
  limit?: number
  offset?: number
  action_id?: ComplexChangeAction
  date_from?: string
  date_to?: string
}

type UrlParams = {
  orgId: number | string
  complexId: number | string
}

type HistoryValue =
  | string
  | number
  | boolean
  | null
  | Array<HistoryValue>
  | Record<string, unknown>

type ChangeApi = {
  id: number | string
  operation: string
  date: string
  old_value?: HistoryValue
  new_value?: HistoryValue
  user?: {
    login?: string
    ip?: string
  }
  source?: string
}

type ResultApi = {
  changes?: Array<ChangeApi> | Record<string, ChangeApi>
  changes_count?: number
}

const formatValue = (value: HistoryValue | undefined): string => {
  if (typeof value === 'boolean') return value ? 'ВКЛ' : 'ВЫКЛ'
  if (Array.isArray(value)) return value.map(formatValue).join('\n')
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'object') {
    const title = value.title ?? value.name ?? value.value
    if (title !== undefined) return formatValue(title as HistoryValue)
    return Object.values(value).map(item => formatValue(item as HistoryValue)).join('\n')
  }
  return String(value)
}

const values = (source?: Array<ChangeApi> | Record<string, ChangeApi>): Array<ChangeApi> => {
  if (!source) return []
  return Array.isArray(source) ? source : Object.values(source)
}

const convertDateParam = (value?: string | Date): string | undefined => {
  if (value instanceof Date) return moment(value).format('YYYY-MM-DD')
  return value
}

export const convertParams = (params: ComplexHistoryRequestParams): [ParamsApi, UrlParams] => [
  Object.fromEntries(
    Object.entries({
      limit: params.limit,
      offset: params.offset,
      action_id: params.actionId,
      date_from: convertDateParam(params.dateFrom),
      date_to: convertDateParam(params.dateTo),
    }).filter(([, value]) => value !== undefined && value !== ''),
  ) as ParamsApi,
  {
    orgId: params.orgId,
    complexId: params.complexId,
  },
]

export const convertResult = (data: ResultApi): ComplexHistory => {
  const items = values(data.changes).map((item): ComplexHistoryItem => {
    const operationDate = moment(item.date)
    const login = item.user?.login || 'Неизвестный пользователь'
    const ip = item.user?.ip

    return {
      id: String(item.id),
      operation: item.operation,
      date: operationDate.isValid()
        ? operationDate.format('DD.MM.YYYY HH:mm')
        : item.date,
      dateSeconds: operationDate.isValid() ? operationDate.unix() : 0,
      oldValue: formatValue(item.old_value),
      newValue: formatValue(item.new_value),
      user: ip ? `${login}, ${ip}` : login,
    }
  })

  return {
    items: items.sort((a, b) => b.dateSeconds - a.dateSeconds),
    count: data.changes_count ?? items.length,
  }
}

export const GET_COMPLEX_CHANGES: Request<
  ComplexHistoryRequestParams,
  ComplexHistory,
  UrlParams
> = [
  'food/schools/{orgId}/complexes/{complexId}/changes',
  'get',
  ['data'],
]
